import React from "react";
import SuiBox from "components/SuiBox";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import ErrorAlert from "components/ErrorAlert";
import CustomSelect from "components/CustomSelect";
import MonthPicker from "components/MonthPicker";
import { Card, CircularProgress, Grid, Icon } from "@mui/material";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import moment from "moment";
import { getUsersReq } from "services/api";
import { isEmpty } from "lodash";
import { getUserAttendanceReportReq } from "services/api";
import TimelineItem from "examples/Timeline/TimelineItem";
import { CHECKED_IN_TYPES } from "constants";
import {
  customSelectStyles,
  REPORT_BUTTON_SX,
  REPORT_SUMMARY_CARDS,
  REPORT_TOOLBAR_SX,
} from "../../reports.constants";
import { useLocation, useSearchParams } from "react-router-dom";
import { TIME_FORMAT } from "constants";
import { toHoursAndMinutes } from "utils/DateAndTime";
import { minsDiff } from "utils/DateAndTime";
import { updateAttendanceDateTimeReq } from "services/api";

function GeneralReport() {
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(moment().format("YYYY-MM"));
  const [attendances, setAttendances] = useState([]);
  const [reportSummary, setReportSummary] = useState(null);
  const { state } = useLocation();
  const [searchParams] = useSearchParams();

  // Store
  const {
    auth: { admin, team, channel },
  } = useSelector((state) => state);

  const getUsers = async () => {
    setIsLoading(true);
    try {
      const admin_id = admin.id;
      const { data } = await getUsersReq({
        admin_id,
        team_id: team.id,
        channel_id: channel.id,
      });
      setUsers(data);

      const paramUserId = searchParams.get("user");
      const prefillUser = state?.value
        ? state
        : paramUserId
        ? data.find((user) => user.value === paramUserId) || { value: paramUserId, label: paramUserId }
        : null;
      const prefillMonth = state?.month || searchParams.get("month") || null;

      if (prefillUser) {
        setSelectedUser(prefillUser);
        if (prefillMonth) setSelectedMonth(prefillMonth);
        generateReport({ user: prefillUser, month: prefillMonth || undefined });
      }
    } catch (err) {
      setErrorMessage(err.response?.data.message);
    }
    setIsLoading(false);
  };

  const generateReport = async (overrides = {}) => {
    const user = overrides.user || selectedUser;
    const month = overrides.month || selectedMonth;
    if (isEmpty(user) || !month) return;
    setIsReportLoading(true);
    try {
      const body = { month };
      const admin_id = admin.id;
      const { data } = await getUserAttendanceReportReq({
        admin_id,
        team_id: team.id,
        channel_id: channel.id,
        user_slack_id: user.value,
        body,
      });
      const reportList = data.report != null ? data.report : data;
      const summary = data.summary != null ? data.summary : null;
      setAttendances(Array.isArray(reportList) ? reportList : []);
      setReportSummary(summary);
    } catch (err) {
      setErrorMessage(err.response?.data.message);
    }
    setIsReportLoading(false);
  };

  const updateAttendanceTime = async ({ recordId, field, newDateTime, breakId }) => {
    try {
      console.log("recordId, field, newDateTime", recordId, field, newDateTime);
      // Convert newDateTime to Unix timestamp
      const updatedTimestamp = moment(newDateTime).unix();

      // Get admin ID from Redux state
      const adminId = admin.id;

      const { data } = await updateAttendanceDateTimeReq({
        admin_id: adminId,
        record_id: recordId,
        field,
        timestamp: updatedTimestamp,
        break_id: breakId,
      });

      console.log("Attendance updated:", data);

      // Update local state
      setAttendances((prev) =>
        prev.map((attendance) => ({
          ...attendance,
          records: attendance.records.map((record) =>
            record.id === recordId ? { ...record, [field]: updatedTimestamp } : record
          ),
        }))
      );

      return data;
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to update attendance");
      console.error(err);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  return (
    <>
      {errorMessage && (
        <ErrorAlert color="error" message={errorMessage} clearError={() => setErrorMessage(null)} />
      )}
      <SuiBox py={3}>
        <SuiBox
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          sx={REPORT_TOOLBAR_SX}
        >
          <SuiBox className="w-340" sx={{ height: "35px" }}>
            {isLoading ? (
              <CircularProgress color="dark" size={18} />
            ) : (
              <CustomSelect
                isSearchable={true}
                customStyles={customSelectStyles}
                options={users}
                value={selectedUser}
                placeholder="Select Employee"
                onChange={(data) => setSelectedUser(data)}
              />
            )}
          </SuiBox>
          <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
          <SuiButton
            variant="gradient"
            color="secondary"
            size="small"
            onClick={() => generateReport()}
            sx={REPORT_BUTTON_SX}
          >
            Generate Report
            <Icon sx={{ fontWeight: "bold" }}>arrow_forward</Icon>
          </SuiButton>
        </SuiBox>
      </SuiBox>

      <SuiBox pb={3}>
        <Grid container spacing={3}>
          {reportSummary != null && attendances.length > 0 && (
            <Grid item xs={12}>
              <SuiBox mb={3}>
                <Grid container spacing={3}>
                  {REPORT_SUMMARY_CARDS.map((card) => (
                    <Grid key={card.name} item xs={12} sm={6} xl={4}>
                      <MiniStatisticsCard
                        title={{ text: card.text }}
                        count={reportSummary[card.name] ?? "—"}
                        icon={{
                          color: card.iconColor,
                          component: card.iconComponent,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </SuiBox>
            </Grid>
          )}
          {isReportLoading ? (
            <Grid item xs={12} md={12} lg={12}>
              <Card className="h-100">
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  p={3}
                >
                  <CircularProgress color="dark" size={30} />
                </SuiBox>
              </Card>
            </Grid>
          ) : attendances.length > 0 ? (
            attendances.map((attendance, index) => {
              let start = 0;
              let duration = 0;
              return (
                <Grid item xs={12} md={6} lg={4} key={`main-${index}`}>
                  <Card className="h-100">
                    <SuiBox pt={3} px={3}>
                      <SuiTypography variant="h6" fontWeight="medium">
                        {moment(attendance.day).format("LL")}
                      </SuiTypography>
                    </SuiBox>
                    <SuiBox p={2} className="report-card-fix-height">
                      {attendance.records.map((record, recordIndex) => {
                        if (recordIndex === 0 || record.from) {
                          start = record.from;
                          duration = minsDiff(record.from, start);
                        } else {
                          if (record.break_from) {
                            duration += minsDiff(record.break_from, start);
                            start = record.break_from;
                          }
                          if (record.break_to) {
                            start = record.break_to;
                          }
                          if (record.to) {
                            duration += minsDiff(record.to, start);
                            start = record.to;
                          }
                        }

                        return (
                          <React.Fragment key={`record-${recordIndex}`}>
                            {record.from && (
                              <TimelineItem
                                color={`${
                                  record.type === CHECKED_IN_TYPES.wfh ? "primary" : "success"
                                }`}
                                icon={`${
                                  record.type === CHECKED_IN_TYPES.wfh ? "home_work" : "business"
                                }`}
                                title={`${recordIndex ? "Checked-In-Again" : "Checked-In"} (${
                                  record.type
                                })`}
                                dateTime={moment.unix(record.from).format(TIME_FORMAT)}
                                comment={record.from_comment}
                                recordTimestamp={record.from} // <-- pass raw timestamp here
                                onDateTimeUpdate={async (newDateTime) => {
                                  try {
                                    const updatedTime = moment(newDateTime).unix();
                                    await updateAttendanceTime({
                                      recordId: record.id,
                                      field: "from",
                                      newDateTime: newDateTime,
                                    });
                                    record.from = updatedTime; // update local state
                                    setAttendances([...attendances]);
                                  } catch (err) {
                                    setErrorMessage("Failed to update time");
                                  }
                                }}
                              />
                            )}
                            {record.break_from && (
                              <TimelineItem
                                color="warning"
                                icon="cookie"
                                title={`Break`}
                                dateTime={moment.unix(record.break_from).format(TIME_FORMAT)}
                                recordTimestamp={record.break_from}
                                comment={record.break_from_comment}
                                onDateTimeUpdate={async (newDateTime) => {
                                  try {
                                    // convert newDateTime to unix timestamp or whatever your API expects
                                    const updatedTime = moment(newDateTime).unix();
                                    await updateAttendanceTime({
                                      recordId: record.id,
                                      field: "break_from",
                                      newDateTime: newDateTime,
                                      breakId: record.break_id,
                                    });
                                    // optionally update local state to reflect changes immediately
                                    record.break_from = updatedTime;
                                    setAttendances([...attendances]);
                                  } catch (err) {
                                    setErrorMessage("Failed to update time");
                                  }
                                }}
                              />
                            )}
                            {record.break_to && (
                              <TimelineItem
                                color="secondary"
                                icon="reply"
                                title={`Back`}
                                dateTime={moment.unix(record.break_to).format(TIME_FORMAT)}
                                recordTimestamp={record.break_to}
                                comment={record.break_to_comment}
                                onDateTimeUpdate={async (newDateTime) => {
                                  try {
                                    // convert newDateTime to unix timestamp or whatever your API expects
                                    const updatedTime = moment(newDateTime).unix();
                                    await updateAttendanceTime({
                                      recordId: record.id,
                                      field: "break_to",
                                      newDateTime: newDateTime,
                                      breakId: record.break_id,
                                    });
                                    // optionally update local state to reflect changes immediately
                                    record.break_to = updatedTime;
                                    setAttendances([...attendances]);
                                  } catch (err) {
                                    setErrorMessage("Failed to update time");
                                  }
                                }}
                              />
                            )}
                            {record.to && (
                              <TimelineItem
                                color="dark"
                                icon="logout"
                                title={`Checked-Out (${record.type})`}
                                dateTime={moment.unix(record.to).format(TIME_FORMAT)}
                                recordTimestamp={record.to}
                                comment={record.to_comment}
                                description={`Duration: ${toHoursAndMinutes(duration)}`}
                                onDateTimeUpdate={async (newDateTime) => {
                                  try {
                                    // convert newDateTime to unix timestamp or whatever your API expects
                                    const updatedTime = moment(newDateTime).unix();
                                    await updateAttendanceTime({
                                      recordId: record.id,
                                      field: "to",
                                      newDateTime: newDateTime,
                                    });
                                    // optionally update local state to reflect changes immediately
                                    record.to = updatedTime;
                                    setAttendances([...attendances]);
                                  } catch (err) {
                                    setErrorMessage("Failed to update time");
                                  }
                                }}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </SuiBox>
                  </Card>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12} md={12} lg={12}>
              <Card className="h-100">
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  p={3}
                >
                  <Icon fontSize="large" color="error" className="cursor-pointer">
                    {"report"}
                  </Icon>
                  <SuiTypography
                    component="span"
                    variant="button"
                    fontWeight="regular"
                    textTransform="capitalize"
                    color={"dark"}
                    opacity={0.5}
                    m={3}
                    sx={{ lineHeight: 0 }}
                  >
                    No records found
                  </SuiTypography>
                </SuiBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </SuiBox>
    </>
  );
}

export default GeneralReport;
